import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  DatePicker,
  Select,
  TextField,
  TextFieldLabel,
} from 'ui-component-library';

import { Container, StyledSvg } from './filter-field.style';
import { createOption } from '../../../../utils/mappers';
import { getFilterSuggestions } from '../../../../utils/api/load-board';
import { showErrorMessage, logger } from '../../../../utils/common';
import { MODE_TYPES } from '../../../../constants';

/**
 * The filter field component
 * @param {object} props - Object containing the props for this component
 * @param {string} props.type - Type of filter field ('select' or 'date')
 * @param {string} props.fieldId - ID of the filter field
 * @param {string} props.label - Label to be displayed above the field
 * @param {object} props.filterOptions - Dropdown options for all select fields
 * @param {object} props.activeFilters - Current active filters on data set
 * @param {func} props.handleUpdateFilters - Function to update filters
 * @return {element}
 */
const FilterField = ({
  type,
  fieldId,
  label,
  filterOptions,
  activeFilters,
  handleUpdateFilters,
}) => {
  const [textValue, setTextValue] = useState(activeFilters[fieldId]);
  const modeType = useSelector((state) => state.modeType?.type);

  /**
   * Hook listening for clear filter changes.
   * If a text filter gets cleared, then clear the local state.
   */
  useEffect(() => {
    if (type === 'text' && !activeFilters[fieldId]) {
      setTextValue();
    }
  }, [activeFilters, type, fieldId]);

  /**
   * Gets the list of options for a filter field
   * @param {string} fieldId - ID of the filter field
   * @return {Array}
   */
  const getOptions = (filterFieldId) => {
    // HACK: Hot fix to acommodate API model changes - Todo: Make more robust for TL LTL statuses
    let id = filterFieldId;
    if (filterFieldId === 'Status') {
      id = modeType === MODE_TYPES.truckload ? 'TlStatus' : 'LtlStatus';
    }
    const options = filterOptions[id] || [];
    return options.map(createOption);
  };

  const handleValueChange = (value) => handleUpdateFilters(fieldId, value);

  /**
   * Updates text filter when the Enter key is pressed
   * @param {Event} event - Keypress event
   * @return {void}
   */
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleUpdateFilters(fieldId, textValue);
    }
  };

  const clearValue = () => {
    setTextValue('');
    handleValueChange(undefined);
  };

  /**
   * Updates text filter when the field is blurred
   * @param {Event} event - Blur event
   * @return {void}
   */
  const handleBlur = () => {
    handleUpdateFilters(fieldId, textValue);
  };

  /**
   * Function to get type ahead suggestions for a particular field
   * @param {string} term - Term to get suggestions for
   * @return {object[]} - Select options of possible filter values
   */
  const fetchTypeAheadOptions = async (term) => {
    const fid = fieldId.charAt(0).toLowerCase() + fieldId.slice(1); // lowercase first char
    try {
      const res = await getFilterSuggestions(fid, term);
      if (res) {
        return res.data.model.map((suggestion) => createOption(suggestion.value));
      }
    } catch (error) {
      logger.error(error);
      showErrorMessage('Unable to get filter suggestions');
    }
    return [];
  };

  const closeIcon = (
    <StyledSvg show={(activeFilters[fieldId])} onClick={clearValue}>
      <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
    </StyledSvg>
  );

  return (
    <Container>
      {type === 'text' && (
        <>
          <TextFieldLabel text={label} boundTo={fieldId} />
          <TextField
            placeholder="Enter filter"
            icon="Search"
            value={textValue}
            onValueChange={(e) => setTextValue(e.target.value || undefined)}
            onKeyPress={handleKeyPress}
            onBlur={handleBlur}
          />
          {closeIcon}
        </>
      )}
      {type === 'type-ahead' && (
        <>
          <TextFieldLabel text={label} boundTo={fieldId} />
          <Select
            placeholder="Enter filter"
            selectedValue={activeFilters[fieldId]}
            options={[]}
            onValueChange={handleValueChange}
            optionLoader={fetchTypeAheadOptions}
            icon="Search"
            isClearable
          />
        </>
      )}
      {type === 'select' && (
        <>
          <TextFieldLabel text={label} />
          <Select
            isClearable
            selectedValue={activeFilters[fieldId]}
            options={getOptions(fieldId)}
            placeholder="No filter(s) selected"
            onValueChange={handleValueChange}
          />
        </>
      )}
      {type === 'date' && (
        <>
          <TextFieldLabel text={label} />
          <DatePicker
            name={fieldId}
            placeholder="Select date"
            selectedDate={activeFilters[fieldId]}
            onValueChange={handleValueChange}
          />
          {closeIcon}
        </>
      )}
    </Container>
  );
};

FilterField.propTypes = {
  type: PropTypes.string.isRequired,
  fieldId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  filterOptions: PropTypes.object,
  // eslint-disable-next-line react/forbid-prop-types
  activeFilters: PropTypes.object.isRequired,
  handleUpdateFilters: PropTypes.func.isRequired,
};

FilterField.defaultProps = {
  filterOptions: {},
};

export default FilterField;
